import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, ExternalLink, Tag, Clock, Grid, List, RefreshCw, Rss, Settings } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { formatArticleText } from '../utils/textUtils';
import { graphqlClient, SEARCH_ARTICLES, GET_TAGS } from '../graphql/client';

const HomePage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortOrder, setSortOrder] = useState('newest');
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalTags, setTotalTags] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  
  // Search-specific state
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchNextToken, setSearchNextToken] = useState(null);
  const [searchTotal, setSearchTotal] = useState(0);

  // GraphQL client handles endpoint and API key dynamically
  // For production, it fetches the API key from Lambda to support key rotation

  // Fetch total tags count from GraphQL API
  const fetchTotalTags = useCallback(async () => {
    try {
      console.log('🏷️ Fetching total tags from GraphQL API...');
      const data = await graphqlClient.query(GET_TAGS);

      if (data && data.getTags) {
        const tags = data.getTags;
        setTotalTags(tags.length);
        console.log(`✅ Loaded ${tags.length} total unique tags from database`);
      } else {
        throw new Error('No tags data received');
      }
    } catch (err) {
      console.error('❌ Error fetching tags:', err);
      // Fallback to calculating from loaded articles if API fails
      setTotalTags(0);
    }
  }, []);

  // Fetch articles from GraphQL API
  const fetchArticles = useCallback(async (isLoadMore = false, token = null) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }

    const query = `
      query GetArticles($nextToken: String) {
        getArticles(limit: 24, nextToken: $nextToken) {
          items {
            id
            title
            description
            link
            published
            author
            site_name
            tags
          }
          total
          nextToken
        }
      }
    `;

    try {
      console.log(`🌐 Fetching articles from GraphQL API... ${isLoadMore ? '(Loading more)' : '(Initial load)'}`);
      const data = await graphqlClient.query(query, token ? { nextToken: token } : {});

      if (data && data.getArticles) {
        const articlesConnection = data.getArticles;
        const rawArticles = articlesConnection.items || [];
        
        // Deduplicate articles by ID to prevent React key warnings and format text
        const uniqueArticles = rawArticles.reduce((acc, article) => {
          if (!acc.find(existing => existing.id === article.id)) {
            // Format article text to fix apostrophe and other HTML entity issues
            const formattedArticle = formatArticleText(article);
            acc.push(formattedArticle);
          }
          return acc;
        }, []);
        
        // Ensure we have an even number of articles for better grid layout
        const evenArticles = uniqueArticles.length % 2 === 0 
          ? uniqueArticles 
          : uniqueArticles.slice(0, -1); // Remove last article if odd number
        
        if (isLoadMore) {
          // Append new articles to existing ones, avoiding duplicates
          setArticles(prevArticles => {
            const combined = [...prevArticles, ...evenArticles];
            const deduped = combined.reduce((acc, article) => {
              if (!acc.find(existing => existing.id === article.id)) {
                acc.push(article);
              }
              return acc;
            }, []);
            
            // Ensure final count is even for better grid layout
            return deduped.length % 2 === 0 ? deduped : deduped.slice(0, -1);
          });
        } else {
          setArticles(evenArticles);
          setTotal(articlesConnection.total || 0);
        }
        
        setNextToken(articlesConnection.nextToken);
        setHasMore(!!articlesConnection.nextToken);
        
        console.log(`✅ Loaded ${evenArticles.length} articles (even number for grid layout) from ${rawArticles.length} total ${isLoadMore ? '(appended)' : `(Total in DB: ${articlesConnection.total || 0})`}`);
      } else {
        throw new Error('No articles data received');
      }
    } catch (err) {
      console.error('❌ Error fetching articles:', err);
      setError(err.message);
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  // Server-side search function
  const performSearch = useCallback(async (searchQuery, token = null) => {
    if (!searchQuery.trim()) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchError(null);
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    
    try {
      console.log(`🔍 Searching for: "${searchQuery}"${token ? ' (loading more)' : ''}`);
      
      const data = await graphqlClient.query(SEARCH_ARTICLES, {
        query: searchQuery,
        limit: 50,
        nextToken: token
      });

      if (data && data.searchArticles) {
        const searchConnection = data.searchArticles;
        const newResults = searchConnection.items || [];
        
        if (token) {
          // Append to existing results
          setSearchResults(prev => [...prev, ...newResults]);
        } else {
          // New search
          setSearchResults(newResults);
          setIsSearchMode(true);
        }
        
        setSearchNextToken(searchConnection.nextToken);
        setSearchTotal(searchConnection.total || 0);
        
        console.log(`✅ Found ${newResults.length} articles${token ? ' (appended)' : ''} (Total matches: ${searchConnection.total || 0})`);
      } else {
        throw new Error('Invalid search response format');
      }
    } catch (err) {
      console.error('❌ Search error:', err);
      setSearchError(`Search failed: ${err.message}`);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        performSearch(searchTerm);
      } else {
        setIsSearchMode(false);
        setSearchResults([]);
        setSearchError(null);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, performSearch]);

  // Initial load
  useEffect(() => {
    fetchArticles();
    fetchTotalTags();
  }, [fetchArticles, fetchTotalTags]);

  // Infinite scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >= 
        document.documentElement.offsetHeight - 1000 // Load when 1000px from bottom
      ) {
        if (isSearchMode) {
          // Load more search results
          if (searchNextToken && !isSearching) {
            performSearch(searchTerm, searchNextToken);
          }
        } else {
          // Load more regular articles
          if (hasMore && !loading && !loadingMore) {
            fetchArticles(true, nextToken);
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, loading, loadingMore, nextToken, fetchArticles, isSearchMode, searchNextToken, isSearching, searchTerm, performSearch]);

  // Helper function to parse different date formats
  const parseDate = (dateString) => {
    if (!dateString) return new Date(0);
    
    try {
      if (dateString.includes('T') && dateString.includes('Z')) {
        return parseISO(dateString);
      }
      
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date;
      }
      
      return new Date();
    } catch (error) {
      console.warn('Failed to parse date:', dateString, error);
      return new Date();
    }
  };

  // Filter and sort articles
  const filteredAndSortedArticles = useMemo(() => {
    // Use search results when in search mode, otherwise use regular articles
    const sourceArticles = isSearchMode ? searchResults : articles;
    
    let filtered = sourceArticles.filter(article => {
      // When in search mode, don't apply search term filter (already filtered server-side)
      const matchesSearch = isSearchMode || !searchTerm || 
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.site_name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesTag = !selectedTag || selectedTag === "All Topics" ||
        (article.tags && article.tags.some(tag => 
          tag.toLowerCase().includes(selectedTag.toLowerCase())
        ));
      
      return matchesSearch && matchesTag;
    });

    // Sort articles (search results are already relevance-sorted, but allow user sorting)
    if (!isSearchMode || sortOrder !== 'newest') {
      filtered.sort((a, b) => {
        const dateA = parseDate(a.published);
        const dateB = parseDate(b.published);
        
        if (sortOrder === 'newest') {
          return dateB - dateA;
        } else {
          return dateA - dateB;
        }
      });
    }

    return filtered;
  }, [articles, searchResults, isSearchMode, searchTerm, selectedTag, sortOrder]);

  const handleRefresh = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading articles...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <p>Error loading articles: {error}</p>
        <button onClick={handleRefresh}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="header-title">
            <h1>RSS Aggregator</h1>
            <p>Stay updated with your favorite feeds</p>
          </div>
          
          <div className="header-stats">
            <div className="stat">
              <span className="stat-number">{total}</span>
              <span className="stat-label">Total Articles</span>
            </div>
            <div className="stat">
              <span className="stat-number">{totalTags}</span>
              <span className="stat-label">Topics</span>
            </div>
          </div>

          <div className="rss-link">
            <a href="/rss" className="rss-feed-link">
              <Rss size={20} />
              <span className="stat-label">RSS Feed</span>
            </a>
          </div>

          <div className="admin-link">
            <a href="/admin" className="rss-feed-link">
              <Settings size={20} />
              <span className="stat-label">Admin</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main">
        {/* Controls */}
        <div className="controls">
          <div className="search-container">
            <Search className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Search all articles in database..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="clear-search-btn"
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          
          {/* Search status */}
          {isSearchMode && (
            <div className="search-status">
              <span>🔍 Searching all articles for "{searchTerm}"</span>
              {searchTotal > 0 && <span> - {searchTotal} matches found</span>}
            </div>
          )}
          
          {/* Search error */}
          {searchError && (
            <div className="search-error">
              ⚠️ {searchError}
            </div>
          )}

          <div className="filters">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="filter-select"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>

            <div className="view-toggle">
              <button
                onClick={() => setViewMode('grid')}
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              >
                <List size={16} />
              </button>
            </div>

            <button onClick={handleRefresh} className="refresh-btn">
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        {/* Articles */}
        <div className={`articles-container ${viewMode}`}>
          {filteredAndSortedArticles.length === 0 ? (
            <div className="no-results">
              <p>No articles found matching your criteria.</p>
            </div>
          ) : (
            filteredAndSortedArticles.map((article) => (
              <article key={article.id} className="article-card">
                <div className="article-header">
                  <h2 className="article-title" style={{
                    width: '100%',
                    maxWidth: '100%',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    overflow: 'hidden'
                  }}>
                    <a 
                      href={article.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        display: 'block',
                        width: '100%',
                        maxWidth: '100%',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {article.title}
                      <ExternalLink className="external-link-icon" size={16} />
                    </a>
                  </h2>
                  
                  <div className="article-meta">
                    <span className="article-source">{article.site_name}</span>
                    <div className="article-date">
                      <Clock size={14} />
                      <span>
                        {formatDistanceToNow(parseDate(article.published), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="article-description">
                  <p>{article.description}</p>
                </div>
                
                {article.tags && article.tags.length > 0 && (
                  <div className="article-tags">
                    <Tag size={14} />
                    {article.tags.map((tag, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}
                        className={`tag ${selectedTag === tag ? 'active' : ''}`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
              </article>
            ))
          )}
        </div>
        
        {/* Loading more indicator - outside articles container */}
        {(loadingMore || isSearching) && (
          <div className="loading-more">
            <div className="loading-spinner"></div>
            <p>{isSearching ? 'Searching...' : 'Loading more articles...'}</p>
          </div>
        )}
        
        {/* End of articles indicator - outside articles container */}
        {((isSearchMode && !searchNextToken) || (!isSearchMode && !hasMore)) && filteredAndSortedArticles.length > 0 && (
          <div className="end-of-articles">
            <p>You've reached the end! 🎉</p>
            <p>
              {isSearchMode 
                ? `Found ${filteredAndSortedArticles.length} of ${searchTotal} matching articles`
                : `Showing ${filteredAndSortedArticles.length} of ${total} total articles`
              }
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="footer">
        <p>&copy; 2025 RSS Aggregator. Built with React and GraphQL.</p>
      </footer>
    </div>
  );
};

export default HomePage;
